import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Stack,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Email as EmailIcon,
  Description as ExcelIcon,
  Storage as StorageIcon,
  Code as CodeIcon,
} from '@mui/icons-material';

import { API_BASE } from '../utils/apiUtils';

// ============================================
// LOGIC MODAL COMPONENT
// ============================================
const LogicModal = ({ kpi, open, onClose }) => {
  if (!kpi) return null;

  const getSourceTypeColor = (type) => {
    if (type?.includes('EMAIL') || type?.includes('Email')) return 'var(--color-warning)';
    if (type?.includes('EXCEL') || type?.includes('Excel')) return 'var(--color-success)';
    if (type?.includes('API')) return 'var(--color-accent-hover)';
    return 'var(--color-text-secondary)';
  };

  const getSourceIcon = (type) => {
    if (type === 'EMAIL_KPI') return <EmailIcon />;
    if (type === 'EXCEL_METRIC') return <ExcelIcon />;
    return <StorageIcon />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'var(--color-accent-hover)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              bgcolor: 'white',
              color: 'var(--color-accent-hover)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '0.875rem',
            }}
          >
            {kpi.cellMapping || 'N/A'}
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {kpi.kpiName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Info Cards */}
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <Paper
            sx={{
              flex: 1,
              minWidth: 200,
              p: 2,
              bgcolor: 'var(--color-info-light)',
              borderRadius: 2,
              border: '1px solid var(--color-accent-light)',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {getSourceIcon(kpi.type)}
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Data Source
              </Typography>
            </Box>
            <Typography fontWeight={700} color="var(--color-accent-hover)">
              {kpi.source?.replace(/_/g, ' ') || 'Unknown'}
            </Typography>
          </Paper>

          <Paper
            sx={{
              flex: 1,
              minWidth: 200,
              p: 2,
              bgcolor: 'var(--color-warning-light)',
              borderRadius: 2,
              border: '1px solid var(--color-warning-border)',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <CodeIcon fontSize="small" />
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Extraction Type
              </Typography>
            </Box>
            <Typography fontWeight={700} color="var(--color-warning-strong)">
              {kpi.type === 'EMAIL_KPI' ? 'Email Data Extraction' : 'Excel Calculation'}
            </Typography>
          </Paper>
        </Box>

        {/* Automation Steps */}
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="var(--color-accent-hover)"
            sx={{ borderBottom: '2px solid var(--color-accent-light)', pb: 1, mb: 2 }}
          >
            Automation Steps / Logic
          </Typography>

          <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
            {kpi.type === 'EMAIL_KPI' ? (
              <>
                <Typography component="li" sx={{ mb: 1.5, color: 'var(--color-neutral-700)' }}>
                  <strong>Source:</strong> Email from {kpi.emailSender || 'sender'}
                </Typography>
                <Typography component="li" sx={{ mb: 1.5, color: 'var(--color-neutral-700)' }}>
                  <strong>Extraction:</strong> Extract value from email body/attachment for cell <strong>{kpi.cellMapping}</strong>
                </Typography>
                {kpi.remarksCell && (
                  <Typography component="li" sx={{ mb: 1.5, color: 'var(--color-neutral-700)' }}>
                    <strong>Remarks ({kpi.remarksCell}):</strong> Extract equipment names or additional info
                  </Typography>
                )}
                <Typography component="li" sx={{ color: 'var(--color-neutral-700)' }}>
                  <strong>Status:</strong> {kpi.status || 'EXTRACTED'}
                </Typography>
              </>
            ) : (
              <>
                <Typography component="li" sx={{ mb: 1.5, color: 'var(--color-neutral-700)' }}>
                  <strong>Source:</strong> Excel file (KPI Actions)
                </Typography>
                <Typography component="li" sx={{ mb: 1.5, color: 'var(--color-neutral-700)' }}>
                  <strong>Calculation:</strong> Compute metric value for cell <strong>{kpi.cellMapping}</strong>
                </Typography>
                <Typography component="li" sx={{ color: 'var(--color-neutral-700)' }}>
                  <strong>Status:</strong> {kpi.status || 'CALCULATED'}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ bgcolor: 'var(--color-neutral-50)', p: 2 }}>
        <Typography variant="caption" color="text.secondary" fontStyle="italic" sx={{ flex: 1 }}>
          This information dictates the requirements for the AI/ML extraction agent.
        </Typography>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: 'var(--color-accent-hover)' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// KPI ROW COMPONENT
// ============================================
const KPIRow = ({ kpi, onOpenModal }) => {
  const isNegative = (kpi.numericValue || 0) > 0;
  const valueColor = isNegative ? 'var(--color-error)' : 'var(--color-success)';

  // Build remarks text from available data
  const getRemarks = () => {
    const parts = [];
    if (kpi.equipmentNames) {
      parts.push(kpi.equipmentNames);
    }
    if (kpi.value && kpi.value !== String(kpi.numericValue || 0)) {
      // If value has additional text beyond the number
      parts.push(kpi.value);
    }
    return parts.length > 0 ? parts.join(' | ') : '—';
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '0.8fr 3fr 2.5fr 1fr 4fr',
        gap: 0.5,
        alignItems: 'stretch',
        borderBottom: '1px solid',
        borderColor: 'divider',
        transition: 'background-color 0.15s',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {/* Cell Mapping */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Chip
          label={kpi.cellMapping || 'N/A'}
          size="small"
          sx={{
            fontWeight: 700,
            bgcolor: 'var(--color-accent-hover)',
            color: 'white',
            fontSize: '0.75rem',
          }}
        />
      </Box>

      {/* KPI Name */}
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" fontWeight={500}>
          {kpi.kpiName}
        </Typography>
      </Box>

      {/* Pre-processing / Logic */}
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'var(--color-neutral-50)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" fontWeight={600} color="var(--color-accent-hover)">
          {kpi.type === 'EMAIL_KPI'
            ? `Email Extraction (${kpi.source?.replace('HSE_KPI_', '').replace('_', ' ') || 'Source'})`
            : 'Excel Calculation'}
        </Typography>
      </Box>

      {/* Count (C) - Clickable */}
      <Box
        onClick={() => onOpenModal(kpi)}
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderRadius: 1,
          transition: 'background-color 0.15s',
          '&:hover': {
            bgcolor: 'var(--color-info-light)',
          },
        }}
      >
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            color: valueColor,
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
          }}
        >
          {kpi.numericValue || 0}
        </Typography>
      </Box>

      {/* Remarks (H) */}
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" color="text.primary">
            {kpi.equipmentNames || '—'}
          </Typography>
          {kpi.remarksCell && (
            <Typography variant="caption" color="text.secondary">
              → Cell: {kpi.remarksCell}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ============================================
// KPI CATEGORY SECTION COMPONENT
// ============================================
const KPICategory = ({ categoryName, kpis, onOpenModal }) => {
  const totalKpis = kpis.length;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Category Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '0.8fr 3fr 2.5fr 1fr 4fr',
          gap: 0.5,
          alignItems: 'center',
          bgcolor: 'var(--color-accent-hover)',
          color: 'white',
          p: 1.5,
        }}
      >
        <Box />
        <Typography variant="subtitle1" fontWeight={700}>
          {categoryName.replace(/_/g, ' ')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Automation Type
        </Typography>
        <Typography variant="body2" fontWeight={600} textAlign="center">
          {totalKpis}
        </Typography>
        <Typography variant="caption" textAlign="right" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          KPIs in this category
        </Typography>
      </Box>

      {/* Table Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '0.8fr 3fr 2.5fr 1fr 4fr',
          gap: 0.5,
          bgcolor: 'var(--color-bg-tertiary)',
          p: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Typography variant="caption" fontWeight={600} textTransform="uppercase" color="text.secondary" textAlign="center">
          Cell
        </Typography>
        <Typography variant="caption" fontWeight={600} textTransform="uppercase" color="text.secondary">
          Process / KPI
        </Typography>
        <Typography variant="caption" fontWeight={600} textTransform="uppercase" color="text.secondary">
          Pre-processing / Logic
        </Typography>
        <Typography variant="caption" fontWeight={600} textTransform="uppercase" color="text.secondary" textAlign="center">
          Count (C)
        </Typography>
        <Typography variant="caption" fontWeight={600} textTransform="uppercase" color="text.secondary">
          Remarks (H)
        </Typography>
      </Box>

      {/* KPI Rows */}
      {kpis.map((kpi, index) => (
        <KPIRow key={kpi.id || index} kpi={kpi} onOpenModal={onOpenModal} />
      ))}
    </Paper>
  );
};

// ============================================
// MAIN KPI DASHBOARD COMPONENT
// ============================================
const KPIDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [reportMonth, setReportMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Load available months on component mount
  useEffect(() => {
    fetchAvailableMonths();
  }, []);

  // Fetch available months
  const fetchAvailableMonths = async () => {
    try {
      const response = await fetch(`${API_BASE}/kpi-dashboard/months`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch available months');

      const data = await response.json();
      if (data.success && data.months.length > 0) {
        setAvailableMonths(data.months);
        const latestMonth = data.months[0];
        setReportMonth(latestMonth);
        fetchDashboardData(latestMonth);
      }
    } catch (err) {
      console.error('Error fetching months:', err);
      setError('Failed to load available months');
    }
  };

  // Fetch dashboard data for selected month
  const fetchDashboardData = async (month) => {
    if (!month) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/kpi-dashboard/data?reportMonth=${month}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch dashboard data');

      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
      } else {
        throw new Error(data.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle month change
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setReportMonth(newMonth);
    fetchDashboardData(newMonth);
  };

  // Handle refresh
  const handleRefresh = () => {
    if (reportMonth) {
      fetchDashboardData(reportMonth);
    }
  };

  // Handle Excel export/download
  const handleDownloadExcel = async () => {
    if (!reportMonth) return;

    setDownloading(true);
    try {
      const response = await fetch(`${API_BASE}/kpi-dashboard/export?reportMonth=${reportMonth}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to export Excel');

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `KPI_Master_Sheet_${reportMonth}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setNotification({ open: true, message: 'Excel file downloaded successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error downloading Excel:', err);
      setError('Failed to download Excel file');
    } finally {
      setDownloading(false);
    }
  };

  // Format month for display
  const formatMonth = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  // Open modal with KPI details
  const handleOpenModal = (kpi) => {
    setSelectedKPI(kpi);
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedKPI(null);
  };

  // Group KPIs by source/category and link H cells (remarks) to C cells
  const getKpisByCategory = () => {
    if (!dashboardData || !dashboardData.kpis) return {};

    // First, build a map of H cell values (remarks)
    // H16 -> value, H23 -> value, etc.
    const remarksByRow = {};
    dashboardData.kpis.forEach(kpi => {
      if (kpi.cellMapping && kpi.cellMapping.startsWith('H')) {
        const rowNum = kpi.cellMapping.replace(/[^0-9]/g, '');
        remarksByRow[rowNum] = kpi.value || kpi.equipmentNames || '';
      }
    });

    // Now group KPIs, excluding H cells (they're remarks, not separate KPIs)
    // and attach remarks from H cells to corresponding C cells
    return dashboardData.kpis
      .filter(kpi => !kpi.cellMapping || !kpi.cellMapping.startsWith('H'))
      .map(kpi => {
        // Get row number from cell mapping (C16 -> 16)
        const rowNum = kpi.cellMapping ? kpi.cellMapping.replace(/[^0-9]/g, '') : '';
        // Attach H cell remarks if available
        const hCellRemarks = remarksByRow[rowNum];
        return {
          ...kpi,
          // Use H cell remarks if available, otherwise use existing equipmentNames
          equipmentNames: hCellRemarks || kpi.equipmentNames || '',
        };
      })
      .reduce((acc, kpi) => {
        const category = kpi.source || 'OTHER';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(kpi);
        return acc;
      }, {});
  };

  const kpisByCategory = getKpisByCategory();
  const categories = Object.keys(kpisByCategory);
  // Count only non-H cells (actual KPIs, not remarks)
  const totalKpis = dashboardData?.kpis?.filter(kpi => !kpi.cellMapping || !kpi.cellMapping.startsWith('H')).length || 0;

  return (
    <Box sx={{ bgcolor: 'var(--color-neutral-50)', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'var(--color-accent-hover)',
            borderRadius: 3,
            p: 3,
            mb: 4,
            color: 'white',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                }}
              >
                <StorageIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={800} letterSpacing="-0.5px">
                  AI Automation KPI Dashboard
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  Click on <strong>Count (C)</strong> or <strong>Score (E)</strong> for Automation Logic
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={2} alignItems="center">
              <Paper sx={{ bgcolor: 'rgba(255,255,255,0.15)', px: 2, py: 1, borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={500}>
                  {formatMonth(reportMonth)} | Total KPIs: {totalKpis}
                </Typography>
              </Paper>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ color: 'white' }}>Report Month</InputLabel>
                <Select
                  value={reportMonth}
                  onChange={handleMonthChange}
                  label="Report Month"
                  sx={{
                    color: 'white',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                    '.MuiSvgIcon-root': { color: 'white' },
                  }}
                >
                  {availableMonths.map((month) => (
                    <MenuItem key={month} value={month}>
                      {formatMonth(month)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Tooltip title="Download Excel">
                <span>
                  <Button
                    variant="contained"
                    startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    onClick={handleDownloadExcel}
                    disabled={!reportMonth || downloading || loading}
                    sx={{
                      bgcolor: 'white',
                      color: 'var(--color-accent-hover)',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'var(--color-bg-tertiary)' },
                    }}
                  >
                    Export
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Refresh Data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Paper>

        {/* Loading State */}
        {loading && (
          <Paper
            elevation={0}
            sx={{
              p: 8,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CircularProgress size={60} thickness={4} sx={{ color: 'var(--color-accent-hover)' }} />
            <Typography variant="h6" color="text.secondary" mt={3}>
              Loading dashboard data...
            </Typography>
          </Paper>
        )}

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Dashboard Content */}
        {!loading && !error && dashboardData && (
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 1000 }}>
              {categories.map((category) => (
                <KPICategory
                  key={category}
                  categoryName={category}
                  kpis={kpisByCategory[category]}
                  onOpenModal={handleOpenModal}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Empty State */}
        {!loading && !error && (!dashboardData || categories.length === 0) && (
          <Paper
            elevation={0}
            sx={{
              p: 8,
              textAlign: 'center',
              borderRadius: 3,
              border: '2px dashed',
              borderColor: 'divider',
            }}
          >
            <InfoIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No KPI Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a month with data or fetch new KPIs from the KPI Extraction page.
            </Typography>
          </Paper>
        )}

        {/* Footer */}
        <Box mt={4} pt={2} borderTop="1px solid" borderColor="divider" textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Data based on "Final KPI Sheet" and corresponding automation logic.
          </Typography>
        </Box>

        {/* Logic Modal */}
        <LogicModal kpi={selectedKPI} open={modalOpen} onClose={handleCloseModal} />

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setNotification({ ...notification, open: false })}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default KPIDashboard;
